import { Component, EventEmitter, Input, Output, OnInit, inject } from "@angular/core";
import { ITags, IBucketItem } from "../models/bucket-item.model";
import { MasterDataService } from "../../../dynamic-controls/master-data-service";
import { map, combineLatest, take } from "rxjs";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import { BucketItemService } from "../service/bucket-item.service";
import { IUpdateTag, IUpdateTagResponse } from "../models/update-tag.model";
import { DataService } from "../service/data.service";
import { RowDataService } from "../service/row-data.service";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { MessageService } from "../../../main";
import { AppSettingsService, SpinnerService } from "../../../core";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorConstants } from "../constants/component-constants";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import { MatFormField, MatInput, MatSuffix } from "@angular/material/input";
import { MatSelect } from "@angular/material/select";
import { MatOption } from "@angular/material/autocomplete";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatTooltip } from "@angular/material/tooltip";
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: "app-package-folder-tags-general",
    templateUrl: "./package-folder-tags-general.component.html",
    styleUrls: ["./package-folder-tags-general.component.scss"],
    imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatFormField, MatSelect, MatOption, MatInput, MatIconButton, MatSuffix, MatTooltip, MatButton]
})
export class PackageFolderTagsGeneralComponent implements OnInit {
  @Input() initialValue: Partial<ITags>[] = [];
  @Output() cancelOperation: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() tagsUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() expandTags: boolean = false;
  @Input() isFromGeneralInformation: boolean = false;

  private readonly controlsService = inject(MasterDataService);
  private readonly breadcrumbDataService = inject(BreadcrumbDataService);
  private readonly bucketItemService = inject(BucketItemService);
  private readonly dataService = inject(DataService);
  private readonly rowDataService = inject(RowDataService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly appSettingsService = inject(AppSettingsService);
  private readonly spinnerService = inject(SpinnerService);

  public selectedTagValues: Partial<ITags>[] = [];
  public originalTagValues: Partial<ITags>[] = [];
  public updateTagsRequest: Partial<IUpdateTag> = {};
  public bucketInfo$ = this.breadcrumbDataService.getCurrentBreadCrumbItem();
  public currentBreadcrumbItem: any = {};
  public userHasInteracted = false;
  public interactedTags: Set<string> = new Set<string>(); // Track which tags have been touched
  private readonly featureToken = "bucket-items";
  private readonly surfaceToken = "package-folder-tags";

  readonly packageFolderTags = toSignal(
    this.controlsService.GetMasterData().pipe(
      map((controls) => {
        return controls
          .filter((control) => {
            const scopeValue = `${control.Scope ?? ''}`.toLowerCase();
            const isPackageFolderScope = scopeValue === 'packagefolderlevel';
            return !control.IsObsolete && isPackageFolderScope;
          })
          .map(control => {
            const all = (control.TagValues || []).map(tv => ({ Code: tv, IsSelected: false }));
            all.sort((a, b) => a.Code.localeCompare(b.Code));
            return {
              ...control,
              AllTagDropDownValues: all,
              TagDropDownValues: [...all]
            };
          })
          .sort((a, b) => a.TagsDisplayOrder - b.TagsDisplayOrder);
      })
    ),
    { initialValue: [] }
  );

  private readonly _packageFolderTagsData$ = this.controlsService.GetMasterData().pipe(
    map((controls) => {
      return controls
        .filter((control) => {
          const scopeValue = `${control.Scope ?? ''}`.toLowerCase();
          const isPackageFolderScope = scopeValue === 'packagefolderlevel';
          return !control.IsObsolete && isPackageFolderScope;
        })
        .map(control => {
          const all = (control.TagValues || []).map(tv => ({ Code: tv, IsSelected: false }));
          all.sort((a, b) => a.Code.localeCompare(b.Code));
          return {
            ...control,
            AllTagDropDownValues: all,
            TagDropDownValues: [...all]
          };
        })
        .sort((a, b) => a.TagsDisplayOrder - b.TagsDisplayOrder);
    })
  );

  public filterTagValues(tag: any, searchValue: string) {
    const s = (searchValue || "").toLowerCase();
    if (!tag.AllTagDropDownValues) {
      tag.TagDropDownValues = [];
      return;
    }
    tag.TagDropDownValues = tag.AllTagDropDownValues.filter((x: any) =>
      x.Code.toLowerCase().includes(s)
    );
  }

  public clearSearchInput(tag: any, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
      const target = ev.target as HTMLElement;
      const optionEl = target.closest('mat-option');
      if (optionEl) {
        const input = optionEl.querySelector('input');
        if (input) {
          input.value = '';
        }
      }
    this.filterTagValues(tag, '');
  }

  ngOnInit(): void {
    this.selectedTagValues = [...this.initialValue];

    this.bucketInfo$.subscribe((bucketInfo) => {
      this.currentBreadcrumbItem = bucketInfo || {};
    });

    // Initialize selectedTagValues with existing tags or empty values for all available tag keys
    this._packageFolderTagsData$.pipe(take(1)).subscribe(tags => {
      
      tags.forEach(tag => {
        const existingTag = this.selectedTagValues.find(x => x.TagKey === tag.TagKey);
        
        if (!existingTag) {
          this.selectedTagValues.push({
            TagKey: tag.TagKey,
            TagValue: '',
            TagsDisplayOrder: tag.TagsDisplayOrder
          });
        }
      });
      
      this.selectedTagValues.sort((a, b) => (a.TagsDisplayOrder || 0) - (b.TagsDisplayOrder || 0));
      
      // Now set originalTagValues after all tags are initialized (deep copy)
      this.originalTagValues = this.selectedTagValues.map(tag => ({
        TagKey: tag.TagKey,
        TagValue: tag.TagValue,
        TagsDisplayOrder: tag.TagsDisplayOrder
      }));
      
      // Mark existing tags with values as already interacted with
      this.originalTagValues.forEach(tag => {
        if (tag.TagValue && tag.TagValue.trim() !== '') {
          this.interactedTags.add(tag.TagKey);
        }
      });


    });
  }

  public setTagValue(selectedTag: Partial<ITags>, tagKey: string) {
    if (this.isQualitySystemTag(tagKey)) {
      return;
    }

    let index = this.selectedTagValues.findIndex((x) => x.TagKey === tagKey);
    if (index >= 0) {
      this.selectedTagValues.splice(index, 1);
    }
    this.selectedTagValues.push(selectedTag);
    this.userHasInteracted = true; // User has interacted by setting a value
    this.interactedTags.add(tagKey); // Mark this tag as interacted with
  }

  public getInitialTagValue(tagKey: string): string {
    const tagValue = this.selectedTagValues.find((x) => x.TagKey === tagKey)?.TagValue;
    return tagValue || '';
  }

  public savePackageFolderTags(): void {
    this.spinnerService.show(true);

    // Check if any tag values have actually changed from original
    const hasChanges = this.selectedTagValues.some(tag => {
      if (this.isQualitySystemTag(tag.TagKey)) {
        return false;
      }

      const originalTag = this.originalTagValues.find(x => x.TagKey === tag.TagKey);
      const originalValue = originalTag?.TagValue || '';
      const currentValue = tag.TagValue || '';
      return originalValue !== currentValue;
    });

    if (!hasChanges) {
      this.spinnerService.show(false);
      this.messageService.alert('Update a tag value first', this.appSettingsService.toastrDisplayTime);
      return;
    }
    
    combineLatest([
      this.bucketInfo$,
      this.dataService.getAllSelectedData(),
      this.rowDataService.getClickedRowData()
    ]).pipe(take(1)).subscribe(([bucketInfo, selectedItems, clickedItem]) => {
      let itemsToProcess: Partial<IBucketItem>[] = [];

      if (this.isFromGeneralInformation) {
        if (clickedItem) {
          itemsToProcess = [clickedItem];
        }
      } else if (selectedItems && selectedItems.length > 0) {
        itemsToProcess = selectedItems;
      } else if (clickedItem) {
        itemsToProcess = [clickedItem];
      }

      if (itemsToProcess.length === 0) {
        this.spinnerService.show(false);
        this.messageService.alert('Please select or click on items to apply tags');
        return;
      }

      const packageFolders = itemsToProcess.filter(item => 
        item.FolderType === 'StudyPackage' || item.FolderType === 'FacilityPackage'
      );

      if (packageFolders.length === 0) {
        this.spinnerService.show(false);
        this.messageService.alert('Please select or click on package folders to apply tags');
        return;
      }

      // Only include tags that have actually changed from their original values
      const changedTags = this.selectedTagValues.filter(tag => {
        if (this.isQualitySystemTag(tag.TagKey)) {
          return false;
        }

        const originalTag = this.originalTagValues.find(x => x.TagKey === tag.TagKey);
        const originalValue = originalTag?.TagValue || '';
        const currentValue = tag.TagValue || '';
        
        // Include only if the value has changed from original
        return originalValue !== currentValue;
      });

      // Only create tags array if there are changed tags
      if (changedTags.length === 0) {
        this.spinnerService.show(false);
        this.messageService.alert('Update a tag value first');
        return;
      }

      // Create tags array with all selected package folders, but only include changed tags
      const tagsArray = packageFolders.map(item => ({
        BucketItemId: item.Id,
        tags: changedTags
      }));

      // Use the first package folder's PackageId (they should all be the same for package folders)
      this.updateTagsRequest = {
         BucketType: bucketInfo.BucketType,
         BUCode: bucketInfo.BUCode,
         Tags: tagsArray,
         PackageId: packageFolders[0].PackageId,
      };
      this.updateTags();
    });
  }

  private updateTags(): void {
  this.bucketItemService.UpdateTags(this.updateTagsRequest).pipe(
    map((res: IUpdateTagResponse) => {
      this.spinnerService.show(false);
      if (res.JustificationRequired) {
        this.dialog.open(JustificationDialogComponent, {
          width: "450px",
          disableClose: true
        }).afterClosed().subscribe(
          (response: { comment: string }) => {
            if (response && response.comment) {
              this.updateTagsRequest.Comment = response.comment;
              this.updateTags();
            }
          }
        );
      } else {
        this.messageService.success(
          res.SuccessfulMessage,
          this.appSettingsService.toastrDisplayTime
        );
        // Emit success to notify parent components
        this.tagsUpdated.emit(true);
        // this.cancelOperation.emit(true);
      }
    })
  ).subscribe({
    error: (error: HttpErrorResponse) => {
      this.spinnerService.show(false);
      if (error.error?.detail !== undefined) {
        this.messageService.alert(error.error.detail, this.appSettingsService.toastrDisplayTime);
      } else {
        this.messageService.alert(
          ErrorConstants.UPDATE_TAGS_FAILED,
          this.appSettingsService.toastrDisplayTime
        );
      }
    }
  });
}


  public cancelPackageFolderTags(): void {
    // Reset to original values and emit cancel
    this.selectedTagValues = [...this.originalTagValues];
    this.cancelOperation.emit(true);
  }

  public clearTagValue(tagKey: string): void {
    if (this.isQualitySystemTag(tagKey)) {
      return;
    }

    const index = this.selectedTagValues.findIndex((x) => x.TagKey === tagKey);
    const currentValue = index >= 0 ? this.selectedTagValues[index].TagValue : '';
    
    if (index >= 0) {
      this.selectedTagValues[index].TagValue = '';
    }
    this.userHasInteracted = true;
    
    // Check if there was originally a value OR if there's currently a value to clear
    const originalTag = this.originalTagValues.find(x => x.TagKey === tagKey);
    const hadOriginalValue = originalTag && originalTag.TagValue && originalTag.TagValue.trim() !== '';
    const hasCurrentValue = currentValue && currentValue.trim() !== '';
    
    if (hadOriginalValue || hasCurrentValue) {
      this.interactedTags.add(tagKey);
    } else {
      this.interactedTags.delete(tagKey);
    }
  }

  public isQualitySystemTag(tagKey?: string): boolean {
    return (tagKey || '').trim().toLowerCase() === 'quality system';
  }

  public onPanelOpened(): void {
    this.expandTags = true;
  }

  public onPanelClosed(): void {
    this.expandTags = false;
  }

  public buildControlId(element: string, context?: string | number): string {
    const tokens = [
      this.featureToken,
      this.surfaceToken,
      this.slugify(element),
    ];
    if (context !== undefined && context !== null) {
      tokens.push(this.formatContextToken(context));
    }
    return tokens.join('-');
  }

  private formatContextToken(value: string | number): string {
    if (typeof value === 'number') {
      return value.toString().padStart(2, '0');
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return this.slugify(value);
    }
    return '00';
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}
